import { useCallback, useEffect, useState } from 'react';

import { Button } from 'design-system';
import { LibraryBig } from 'lucide-react';
import { useForwardCatalog, useSpotCatalog } from '@/features/catalog';
import { CatalogModal } from '@/features/dashboard/components/AppForm/AppForm/components/CatalogModal';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import type { TabSymbol } from '@/features/dashboard/components/AppForm/AppForm/context/types';
import { AppType } from '@/features/dashboard/types';
import { clearPendingAppSymbols, setSymbols } from '@/features/symbol-selector';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import DragDropContainer from './DragDropContainer';

export const ItemSelection = () => {
  const { addSymbols, maxItems, form } = useAppForm();
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const pendingAppSymbols = useAppSelector(s => s.symbolSelector.pendingAppSymbols);
  const { data: spotCatalog } = useSpotCatalog();
  const { data: forwardCatalog } = useForwardCatalog();

  // Auto-inject symbols when this form was opened from the selector's "Create app" flow
  useEffect(() => {
    const type = form.state.values?.type;
    const isForwardCurves = type === AppType.FWD;
    const catalog = isForwardCurves ? forwardCatalog : spotCatalog;
    if (!pendingAppSymbols?.length || !catalog) return;

    // For FWD charts the selector stores one SymbolKey per period for each selected curve.
    // Group by curve key (Code|PeriodType|TimeRef) and take the first of each group so that
    // addSymbols() receives exactly one entry per curve.
    const sourceSymbols = isForwardCurves
      ? Object.values(
          Object.groupBy(pendingAppSymbols, s => `${s.Code}|${s.PeriodType}|${s.TimeRef}`)
        ).flatMap(group => (group?.[0] ? [group[0]] : []))
      : pendingAppSymbols;

    const symbolsToAdd: TabSymbol[] = sourceSymbols
      .map(symbol => {
        const spot = catalog.find(item => {
          const baseMatch =
            item.Symbol.Code === symbol.Code &&
            item.Symbol.PeriodType === symbol.PeriodType &&
            item.Symbol.TimeRef === symbol.TimeRef;
          if (!baseMatch) return false;
          return isForwardCurves ? true : item.Symbol.Period === symbol.Period;
        });
        if (!spot) return null;
        return { shortTitle: spot.Title || '', symbol, group: 'Prod_Indexes' as const };
      })
      .filter(Boolean) as TabSymbol[];

    if (symbolsToAdd.length) {
      addSymbols(symbolsToAdd, maxItems);
    }
    dispatch(clearPendingAppSymbols());
  }, [
    pendingAppSymbols,
    spotCatalog,
    forwardCatalog,
    form.state.values?.type,
    addSymbols,
    maxItems,
    dispatch
  ]);

  const handleClick = useCallback(() => {
    const symbols = form.state.values?.symbols;
    dispatch(
      setSymbols(
        symbols
          ? [
              ...symbols.active.map(symbol => symbol.symbol),
              ...symbols.backlog.map(symbol => symbol.symbol)
            ]
          : []
      )
    );
    setOpen(true);
  }, [dispatch, form.state.values?.symbols]);
  return (
    <form.Subscribe selector={s => s.values?.type}>
      {type => (
        <section className='flex flex-col rounded-xl bg-card p-4'>
          <div className='mb-6 flex flex-1 justify-between'>
            <div className='flex flex-1 flex-col'>
              <div className='flex flex-1 justify-between gap-4'>
                <div>
                  <h3 className='mb-2 text-sm font-semibold'>
                    {type === AppType.FWD ? 'Curve' : 'Item'} selection
                  </h3>
                  <p className='mb-4 text-xs font-medium text-muted-foreground'>
                    Browse and select {type === AppType.FWD ? 'curves' : 'items'} for your app
                  </p>
                </div>
                <Button
                  className='w-[180px]'
                  variant='secondary'
                  onClick={handleClick}
                >
                  <LibraryBig aria-hidden='true' />
                  Add from catalog
                </Button>
              </div>

              <DragDropContainer
                itemsName={AppType.FWD === type ? 'curve' : 'item'}
                maxCapacity={maxItems}
                setOpen={setOpen}
              />
            </div>
          </div>
          {open && (
            <CatalogModal
              addItemsToBacklog={symbols => {
                addSymbols(symbols, maxItems);
              }}
              setOpen={setOpen}
            />
          )}
        </section>
      )}
    </form.Subscribe>
  );
};
